/**
 * AdminWholesaleTab — platform master switch + per-seller toggle list.
 * Used inside AdminPage (tab id: "wholesale").
 *
 * Props: { flash } from the parent for success/error toasts.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Tag, Search, Power, PowerOff } from "lucide-react";
import { api, formatApiError } from "../lib/api";

export default function AdminWholesaleTab({ flash }) {
  const [platform, setPlatform] = useState(null);   // {enabled: bool} or null
  const [sellers, setSellers] = useState([]);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/admin/wholesale/settings"),
        api.get("/admin/wholesale/sellers"),
      ]);
      setPlatform(a.data);
      setSellers(b.data || []);
    } catch (e) { flash(formatApiError(e?.response?.data?.detail) || "Load failed", true); }
  }, [flash]);

  useEffect(() => { load(); }, [load]);

  const togglePlatform = async () => {
    if (!platform) return;
    const target = !platform.enabled;
    try {
      const { data } = await api.put("/admin/wholesale/settings", { enabled: target });
      setPlatform(data);
      flash(target ? "Wholesale enabled across platform." : "Wholesale disabled across platform.");
    } catch (e) { flash(formatApiError(e?.response?.data?.detail) || "Toggle failed", true); }
  };

  const toggleSeller = async (s) => {
    setBusyId(s.user_id);
    const target = !s.wholesale_enabled;
    try {
      await api.put(`/admin/sellers/${s.user_id}/wholesale`, { enabled: target });
      setSellers(arr => arr.map(x => x.user_id === s.user_id ? { ...x, wholesale_enabled: target } : x));
      flash(`${s.email}: wholesale ${target ? "enabled" : "disabled"}.`);
    } catch (e) { flash(formatApiError(e?.response?.data?.detail) || "Toggle failed", true); }
    finally { setBusyId(null); }
  };

  const filtered = sellers.filter(s =>
    !q ? true :
    (s.email || "").toLowerCase().includes(q.toLowerCase()) ||
    (s.name || "").toLowerCase().includes(q.toLowerCase())
  );

  const totalOn = sellers.filter(s => s.wholesale_enabled).length;

  return (
    <div className="space-y-4" data-testid="admin-wholesale-tab">
      {/* Platform master switch card */}
      <div className="card p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#D4FF00]/10 flex items-center justify-center shrink-0">
            <Tag size={18} className="text-[#D4FF00]"/>
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-lg uppercase">Wholesale Master Switch</div>
            <div className="text-xs text-[#A1A1AA] mt-1">
              When OFF, no seller&apos;s bulk-discount slabs apply at checkout — regardless of per-seller toggle.
              Useful for incidents, holiday sales, or pricing experiments.
            </div>
            <div className="text-[10px] text-[#A1A1AA] mt-2">
              Currently: <span className={`font-bold ${platform?.enabled ? "text-[#D4FF00]" : "text-[#FF007F]"}`}>
                {platform == null ? "loading…" : platform.enabled ? "ENABLED platform-wide" : "DISABLED platform-wide"}
              </span>
              {" · "}{totalOn} of {sellers.length} sellers have wholesale enabled.
            </div>
          </div>
          <button onClick={togglePlatform}
            disabled={platform == null}
            data-testid="admin-wholesale-platform-toggle"
            className={`px-4 py-2 rounded-xl font-bold uppercase tracking-widest text-xs transition shrink-0 ${
              platform?.enabled
                ? "bg-[#D4FF00] text-black hover:opacity-90"
                : "bg-[#FF007F] text-white hover:opacity-90"
            }`}>
            {platform?.enabled ? "Disable platform" : "Enable platform"}
          </button>
        </div>
      </div>

      {/* Per-seller list */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
          <div className="font-display font-bold uppercase text-sm">Per-seller wholesale</div>
          <div className="text-xs text-[#A1A1AA] flex-1">
            {filtered.length} of {sellers.length} sellers shown
          </div>
          <div className="relative w-full sm:w-64">
            <Search size={14} className="absolute left-3 top-2.5 text-[#A1A1AA]"/>
            <input value={q} onChange={e => setQ(e.target.value)}
              data-testid="admin-wholesale-search"
              placeholder="Filter by name or email…"
              className="input-dark pl-8 pr-3 py-2 text-xs w-full"/>
          </div>
        </div>

        {filtered.length === 0 && (
          <div className="text-center text-sm text-[#A1A1AA] py-8">No sellers match this filter.</div>
        )}

        <div className="divide-y divide-[#3D2845]">
          {filtered.map(s => {
            const on = s.wholesale_enabled;
            const busy = busyId === s.user_id;
            return (
              <div key={s.user_id} className="flex items-center gap-3 py-2.5"
                data-testid={`admin-wholesale-seller-${s.user_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{s.name || s.email}</div>
                  <div className="text-[10px] text-[#A1A1AA] truncate">{s.email}</div>
                </div>
                <span className={`text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded shrink-0 ${
                  on ? "bg-[#D4FF00]/15 text-[#D4FF00]" : "bg-[#FF007F]/15 text-[#FF007F]"
                }`}>
                  {on ? "Enabled" : "Disabled"}
                </span>
                <button onClick={() => toggleSeller(s)}
                  disabled={busy || !platform?.enabled}
                  data-testid={`admin-wholesale-toggle-${s.user_id}`}
                  title={!platform?.enabled ? "Platform master switch is OFF — enable that first" : (on ? "Disable for this seller" : "Enable for this seller")}
                  className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition disabled:opacity-30 ${
                    on
                      ? "bg-[#3D2845] hover:bg-[#FF007F] hover:text-white"
                      : "bg-[#3D2845] hover:bg-[#D4FF00] hover:text-black"
                  }`}>
                  {busy ? <span className="text-[10px]">…</span> : (on ? <PowerOff size={14}/> : <Power size={14}/>)}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
